import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  List,
  X,
  CalendarPlus,
  CaretDown,
  Basketball,
  SwimmingPool,
  Users,
  Buildings,
  Envelope,
  InstagramLogo,
  Drop,
} from '@phosphor-icons/react';

// Trimmed to 3 links + Quick Book CTA — Home is the logo, FAQ lives in the footer
const navLinks = [
  { name: 'Services', path: '/services', icon: Buildings },
  { name: 'Scholarship', path: '/scholarship', icon: Drop },
  { name: 'About', path: '/about', icon: Users },
  { name: 'Contact', path: '/contact', icon: Envelope },
];

const quickBookSports = [
  { name: 'Water Aerobics', icon: Drop, color: 'text-cyan-600', to: '/water-aerobics' },
  { name: 'Private Swim', icon: SwimmingPool, color: 'text-sky-500', to: '/book?sport=swimming' },
  { name: 'Basketball', icon: Basketball, color: 'text-blue-600', to: '/book?sport=basketball' },
];

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [quickBookOpen, setQuickBookOpen] = useState(false);
  const location = useLocation();
  const quickBookRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setQuickBookOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (quickBookRef.current && !quickBookRef.current.contains(e.target as Node)) {
        setQuickBookOpen(false);
      }
    };
    if (quickBookOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [quickBookOpen]);

  // Only the home page has a dark hero behind the nav — every other page
  // shows the white-glass nav from the top so text stays readable.
  const isHome = location.pathname === '/';
  const overDarkHero = isHome && !scrolled;

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 60, damping: 18 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          overDarkHero
            ? 'bg-transparent'
            : 'bg-white/80 backdrop-blur-glass border-b border-slate-200/60 shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src="/logo-btt.svg"
                alt="Better Together Training"
                className="w-9 h-9 transition-transform duration-300 group-hover:scale-105 drop-shadow-md"
              />
              <div className="flex flex-col leading-tight">
                <span
                  className={`font-display text-xl sm:text-2xl font-bold tracking-tightest whitespace-nowrap transition-colors duration-300 ${
                    overDarkHero ? 'text-white drop-shadow-md' : 'text-slate-900'
                  }`}
                >
                  Better Together Training
                </span>
                <span
                  className={`text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] whitespace-nowrap uppercase transition-colors duration-300 ${
                    overDarkHero ? 'text-white/85' : 'text-slate-500'
                  }`}
                >
                  Family Sports &amp; Fitness
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-5 py-2.5 rounded-full text-base font-semibold transition-colors duration-200 ${
                      overDarkHero
                        ? isActive
                          ? 'text-white'
                          : 'text-white/80 hover:text-white'
                        : isActive
                          ? 'text-blue-700'
                          : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className={`absolute inset-0 -z-10 rounded-full transition-colors ${
                          overDarkHero ? 'bg-white/15 backdrop-blur-sm' : 'bg-blue-50'
                        }`}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Right side: Quick Book + Mobile Toggle */}
            <div className="flex items-center gap-2">
              {/* Quick Book Dropdown — desktop */}
              <div className="hidden lg:block relative" ref={quickBookRef}>
                <button
                  type="button"
                  onClick={() => setQuickBookOpen((o) => !o)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-blue-600 to-blue-700 text-white font-semibold rounded-full text-base shadow-sport-blue hover:shadow-lg transition-shadow"
                >
                  <CalendarPlus size={18} weight="fill" />
                  <span>Quick Book</span>
                  <CaretDown
                    size={14}
                    weight="bold"
                    className={`transition-transform ${quickBookOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence>
                  {quickBookOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                      className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-premium border border-slate-100 overflow-hidden"
                    >
                      <div className="p-2">
                        <p className="px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Pick your sport
                        </p>
                        {quickBookSports.map((sport) => (
                          <Link
                            key={sport.to}
                            to={sport.to}
                            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors group"
                          >
                            <span className={`flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 ${sport.color}`}>
                              <sport.icon size={22} weight="duotone" />
                            </span>
                            <span className="font-semibold text-slate-800 group-hover:text-blue-700 transition-colors">
                              {sport.name}
                            </span>
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/book"
                        className="flex items-center justify-between px-5 py-3 bg-slate-50 border-t border-slate-100 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                      >
                        <span>Returning athlete? Book again →</span>
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile menu trigger */}
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="lg:hidden p-3 rounded-full bg-white/80 backdrop-blur-glass border border-slate-200 text-slate-800 hover:bg-white transition-colors"
                aria-label="Open menu"
              >
                <List size={22} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Full-Screen Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] lg:hidden"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900"
              onClick={() => setIsOpen(false)}
            />
            {/* Grain overlay */}
            <div className="absolute inset-0 bg-noise opacity-[0.04] mix-blend-overlay pointer-events-none" />

            {/* Content */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 90, damping: 18 }}
              className="relative h-full flex flex-col overflow-y-auto"
            >
              {/* Top bar */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-2.5">
                  <img src="/logo-btt.svg" alt="" className="w-9 h-9 drop-shadow-md" />
                  <span className="font-display text-lg font-bold text-white tracking-tighter">
                    Better Together Training
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-3 rounded-full bg-white/10 border border-white/20 text-white"
                  aria-label="Close menu"
                >
                  <X size={22} weight="bold" />
                </button>
              </div>

              {/* Nav links */}
              <div className="flex-1 px-6 py-8">
                <p className="text-[11px] font-semibold text-blue-200/80 uppercase tracking-[0.2em] mb-4">
                  Menu
                </p>
                <ul className="space-y-1">
                  {navLinks.map((link, i) => (
                    <motion.li
                      key={link.path}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.06, type: 'spring', stiffness: 80, damping: 16 }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-between py-4 border-b border-white/10 group ${
                          location.pathname === link.path ? 'text-cyan-300' : 'text-white'
                        }`}
                      >
                        <span className="font-display text-3xl font-bold tracking-tightest">
                          {link.name}
                        </span>
                        <link.icon
                          size={28}
                          weight={location.pathname === link.path ? 'fill' : 'duotone'}
                          className="opacity-60 group-hover:opacity-100 transition-opacity"
                        />
                      </Link>
                    </motion.li>
                  ))}
                </ul>

                <p className="text-[11px] font-semibold text-blue-200/80 uppercase tracking-[0.2em] mt-10 mb-4">
                  Quick Book
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {quickBookSports.map((sport, i) => (
                    <motion.div
                      key={sport.to}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + i * 0.05, type: 'spring', stiffness: 100, damping: 14 }}
                    >
                      <Link
                        to={sport.to}
                        onClick={() => setIsOpen(false)}
                        className="flex flex-col items-center justify-center gap-2 py-5 rounded-2xl bg-white/10 backdrop-blur-glass border border-white/15 hover:bg-white/15 transition-colors"
                      >
                        <sport.icon size={32} weight="duotone" className="text-cyan-200" />
                        <span className="text-xs font-semibold text-white">{sport.name}</span>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Footer of overlay — socials */}
              <div className="px-6 py-6 border-t border-white/10 safe-area-bottom">
                <p className="text-[11px] font-semibold text-blue-200/80 uppercase tracking-[0.2em] mb-3">
                  Follow Us
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.instagram.com/tootall614"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/15 text-white text-sm font-semibold"
                  >
                    <InstagramLogo size={18} weight="fill" />
                    @tootall614
                  </a>
                  <a
                    href="https://www.instagram.com/sukisinclair.robinson"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/15 text-white text-sm font-semibold"
                  >
                    <InstagramLogo size={18} weight="fill" />
                    @sukisinclair
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
